// Angular Imports
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	OnInit,
	inject,
	signal,
} from '@angular/core'
import { NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
// Angular Material Imports
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
// Shared Imports
import { ButtonComponent } from '@shared/components'
import { SummaryPanelComponent } from '../../summary-panel/summary-panel.component'
import { IconComponent } from '../../../components/icons/icon/icon.component'
import { EIcons, ISummaryPanelData } from '@shared/models'
import { AuthStore } from '@shared/store'
// Thirdparty Imports

const sharedComponents = [ButtonComponent, IconComponent, SummaryPanelComponent]

@Component({
	standalone: true,
	selector: 'app-alert',
	imports: [NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, MatDialogModule, ...sharedComponents],
	templateUrl: './alert.component.html',
	styleUrls: ['./alert.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertComponent implements OnInit, AfterViewInit {
	readonly #destroyRef = inject(DestroyRef)
	readonly #cdr = inject(ChangeDetectorRef)
	readonly #dialogRef = inject(MatDialogRef<AlertComponent>)
	readonly #dialogData: IDialogData = inject(MAT_DIALOG_DATA)
	readonly EIcons = EIcons
	readonly EAlertType = EAlertType

	/** Who was logged in and when — so error screenshots identify the user (#57730).
	 * Intentionally a snapshot taken when the alert opens, like the timestamp. */
	readonly sessionEmail = inject(AuthStore).knownUserEmail()
	readonly sessionTimestamp = new Date().toLocaleString('es-MX', {
		day: '2-digit',
		month: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	})

	dialogData = signal<IDialogData>({
		type: EAlertType.success,
		message: '-',
		successButtonText: '-',
		cancelButtonText: '-',
		hasCancelButton: false,
		hasSuccessButton: true,
		summary: undefined,
	})

	ngOnInit(): void {
		this.#dialogRef
			.backdropClick()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: () => {
					this.onCloseDialog({ isSuccess: false })
				},
			})
	}

	ngAfterViewInit(): void {
		const dialogData = {
			...this.#dialogData,
			hasSuccessButton: this.#dialogData?.hasSuccessButton ?? true,
		}
		this.dialogData.set(dialogData)
		this.#cdr.detectChanges()
	}

	onCloseDialog({ isSuccess = false }: { isSuccess: boolean }): void {
		this.#dialogRef.close({ isSuccess })
	}
}

enum EAlertType {
	success = 'success',
	error = 'error',
	warning = 'warning',
	info = 'info',
}

interface IDialogData {
	type: keyof typeof EAlertType
	message: string
	successButtonText?: string
	cancelButtonText?: string
	hasCancelButton?: boolean
	hasSuccessButton?: boolean
	summary?: ISummaryPanelData
}
